import { updateImageAction } from 'editor/src/store/gallery/slice';
import { ImageState, MimeType } from 'editor/src/store/gallery/types';
import hasCurrentPrintDetail from 'editor/src/store/gallery/utils/hasCurrentPrintDetail';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { captureException } from 'editor/src/logger';
import { analyzePrintDetailSteps } from 'editor/src/util/printTechAnalysis/analyzePrintDetail';
import { loadRastersFromUrl, MAX_ANALYSIS_SIZE } from 'editor/src/util/printTechAnalysis/loadRasterFromUrl';

// Large enough that a partner's minimum line width spans a few raster pixels on a garment-sized print;
// the side of the square whose pixel count the thin-part rasters keep to.
const PRINT_DETAIL_RASTER_SIZE = 1536;
const IDLE_TIMEOUT_MS = 1000;
// A pass outlasts any idle period, so it waits for one that starts with at least this much left.
const MIN_IDLE_MS = 10;

// Per store (thunk getState is the store's own): a second editor waiting on the first one's
// measurement would never get the result, which is dispatched to the first store only.
const pendingKeysByStore = new WeakMap<() => RootState, Set<string>>();
function getPendingKeys(getState: () => RootState) {
  let pendingKeys = pendingKeysByStore.get(getState);
  if (!pendingKeys) {
    pendingKeys = new Set();
    pendingKeysByStore.set(getState, pendingKeys);
  }
  return pendingKeys;
}
// The watcher asks again on every design change. A URL that fails the same way every time is not
// tried again; one whose request failed or stalled is, a few times. A stalled load holds up every
// image queued after it for the whole timeout, so it gets one retry only.
const unreadableUrls = new Set<string>();
const MAX_FAILED_LOADS = { timeout: 2, error: 3 };
const failedLoads = new Map<string, { timeout: number; error: number }>();
const canRetry = (url: string) => {
  const failed = failedLoads.get(url);
  return !failed || (failed.timeout < MAX_FAILED_LOADS.timeout && failed.error < MAX_FAILED_LOADS.error);
};
// A measurement takes a few hundred ms of main-thread work, so images are measured one at a time
// and each hands the thread back between its passes.
let queue: Promise<void> = Promise.resolve();

function waitForIdle(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.requestIdleCallback === 'function') {
      // One deadline for the whole wait: a short idle period asks again, but only for the time left.
      const deadline = Date.now() + IDLE_TIMEOUT_MS;
      const onIdle = (idle: IdleDeadline) => {
        const left = deadline - Date.now();
        if (idle.didTimeout || left <= 0 || idle.timeRemaining() >= MIN_IDLE_MS) {
          resolve();
        } else {
          window.requestIdleCallback(onIdle, { timeout: left });
        }
      };
      window.requestIdleCallback(onIdle, { timeout: IDLE_TIMEOUT_MS });
    } else {
      // Without idle callbacks a pass goes after the next frame. A hidden tab paints none, so there it
      // goes at once; a visible tab, however busy, waits for its frame.
      let waiting = true;
      const go = () => {
        if (waiting) {
          waiting = false;
          document.removeEventListener('visibilitychange', onHidden);
          resolve();
        }
      };
      const onHidden = () => {
        if (document.visibilityState === 'hidden') {
          go();
        }
      };
      window.requestAnimationFrame(() => setTimeout(go, 0));
      if (document.visibilityState === 'hidden') {
        setTimeout(go, 0);
      } else {
        document.addEventListener('visibilitychange', onHidden);
      }
    }
  });
}

async function measure(imageId: string, url: string, dispatch: ThunkDispatch, getState: () => RootState) {
  const isCurrentUrl = () => getState().gallery.images.find((img) => img.id === imageId)?.url === url;
  if (!isCurrentUrl()) {
    return;
  }

  const loaded = await loadRastersFromUrl(
    url,
    [
      { maxSize: PRINT_DETAIL_RASTER_SIZE, area: true, nearest: true },
      { maxSize: MAX_ANALYSIS_SIZE },
      { maxSize: PRINT_DETAIL_RASTER_SIZE, area: true, maxAlpha: true },
    ],
    { pause: waitForIdle, isCurrent: isCurrentUrl },
  );
  if ('failure' in loaded) {
    if (loaded.failure === 'cancelled') {
      return;
    }
    if (loaded.failure === 'unreadable') {
      unreadableUrls.add(url);
    } else {
      const failed = failedLoads.get(url) ?? { timeout: 0, error: 0 };
      failedLoads.set(url, { ...failed, [loaded.failure]: failed[loaded.failure] + 1 });
    }
    // the image goes without its print checks for the session: reported once, when it is given up
    if (unreadableUrls.has(url) || !canRetry(url)) {
      captureException(new Error(`Image print detail given up: ${loaded.failure}`), { extra: { url } });
    }
    return;
  }
  const [detailRaster, gradientRaster, solidRaster] = loaded.rasters;
  const steps = analyzePrintDetailSteps(detailRaster, gradientRaster, solidRaster);
  let step: ReturnType<typeof steps.next>;
  do {
    // eslint-disable-next-line no-await-in-loop
    await waitForIdle();
    // a replaced image is measured again under its new URL
    if (!isCurrentUrl()) {
      return;
    }
    step = steps.next();
  } while (!step.done);
  dispatch(updateImageAction({ id: imageId, printDetail: { ...step.value, url } }));
}

const analyzeImagePrintDetailOperation =
  (imageId: string) =>
  (dispatch: ThunkDispatch, getState: () => RootState): Promise<void> => {
    const image = getState().gallery.images.find((img) => img.id === imageId);
    // an image still uploading shows its local preview, which is swapped for the stored file once uploaded
    if (
      !image?.url ||
      image.state !== ImageState.UPLOADED ||
      image.type === MimeType.SVG ||
      hasCurrentPrintDetail(image) ||
      unreadableUrls.has(image.url) ||
      !canRetry(image.url)
    ) {
      return Promise.resolve();
    }
    const { url } = image;
    const pendingKey = `${imageId} ${url}`;
    const pendingKeys = getPendingKeys(getState);
    if (pendingKeys.has(pendingKey)) {
      return Promise.resolve();
    }

    pendingKeys.add(pendingKey);
    const run = queue
      .then(waitForIdle)
      .then(() => measure(imageId, url, dispatch, getState))
      .catch((error) => {
        // it would throw again on every design change
        unreadableUrls.add(url);
        captureException(error, { extra: { url } });
      })
      .finally(() => pendingKeys.delete(pendingKey));
    queue = run;
    return run;
  };

export default analyzeImagePrintDetailOperation;
